import type { Farmer } from '../types';
import * as ekycApi from '../api/ekyc';
import { KeyedAsyncCache } from './asyncCache';

const STATUS_TO_BACKEND: Record<Farmer['status'], ekycApi.BackendFarmerStatus> = {
  'Hoạt động': 'ACTIVE',
  'Tạm dừng': 'PAUSED',
  'Ngừng': 'STOPPED',
};
const STATUS_FROM_BACKEND: Record<ekycApi.BackendFarmerStatus, Farmer['status']> = {
  ACTIVE: 'Hoạt động',
  PAUSED: 'Tạm dừng',
  STOPPED: 'Ngừng',
};

const HTX_STATUS_TO_BACKEND: Record<NonNullable<Farmer['htxStatus']>, ekycApi.BackendHtxRelationStatus> = {
  'Đang hoạt động': 'ACTIVE',
  'Cần chú ý': 'NEEDS_ATTENTION',
  'Nguy cơ': 'AT_RISK',
  'Tạm ngưng': 'SUSPENDED',
  'Không đủ điều kiện vay': 'INELIGIBLE',
};
const HTX_STATUS_FROM_BACKEND: Record<ekycApi.BackendHtxRelationStatus, NonNullable<Farmer['htxStatus']>> = {
  ACTIVE: 'Đang hoạt động',
  NEEDS_ATTENTION: 'Cần chú ý',
  AT_RISK: 'Nguy cơ',
  SUSPENDED: 'Tạm ngưng',
  INELIGIBLE: 'Không đủ điều kiện vay',
};

function toFrontendFarmer(f: ekycApi.BackendFarmer): Farmer {
  return {
    id: f._id,
    code: f.code || '',
    name: f.fullName,
    phone: f.phone || '',
    address: f.address || '',
    tinhThanh: f.province || '',
    huyen: f.district || '',
    xa: f.ward || '',
    dienTich: f.areaHa || 0,
    soThanhVien: f.householdMembers || 0,
    loaiCay: f.mainCrop || '',
    ngayBatDau: f.startDate || '',
    status: STATUS_FROM_BACKEND[f.status] || 'Hoạt động',
    htxStatus: HTX_STATUS_FROM_BACKEND[f.htxRelationStatus],
    membershipStatus: ekycApi.toFrontendMembershipStatus(f.membershipStatus),
    membershipRejectedReason: f.membershipRejectedReason,
    membershipInitiatedBy: f.membershipInitiatedBy,
    htxId: f.htxId,
    createdWithoutAccount: f.createdWithoutAccount,
    attachments: f.attachments,
    ghiChu: f.notes,
    userId: f.userId,
    kycStatus: ekycApi.toFrontendKycStatus(f.ekycStatus),
    cccdMask: f.cccdMask,
    ocrName: f.ocrName,
    ocrDob: f.ocrDob,
    ocrGender: f.ocrGender,
    ocrAddress: f.ocrAddress,
    ocrIssueDate: f.ocrIssueDate,
  };
}

const byId = new KeyedAsyncCache<Farmer | null>(
  async (farmerId) => toFrontendFarmer(await ekycApi.getFarmerById(farmerId)),
  null,
  'farmers:byId',
);

interface HtxFarmerList {
  items: Farmer[];
  total: number;
}

const byHtx = new KeyedAsyncCache<HtxFarmerList>(
  async (htxId) => {
    const result = await ekycApi.listFarmersByHtx(htxId, 1, 200);
    return { items: result.items.map(toFrontendFarmer), total: result.total };
  },
  { items: [], total: 0 },
  'farmers:byHtx',
);

/** Full HTX member roster — the real backend has no unscoped "list all farmers"
 * endpoint (only by-HTX or by-id), unlike the old mock's `getFarmers()`. */
export function getFarmersByHtx(htxId?: string): Farmer[] {
  if (!htxId) return [];
  return byHtx.get(htxId).items;
}

export function getFarmerById(farmerId?: string): Farmer | undefined {
  if (!farmerId) return undefined;
  return byId.get(farmerId) ?? undefined;
}

/** Re-reads one farmer (and the HTX roster) after a change made elsewhere —
 * e.g. an eKYC submitted on the farmer's behalf. */
export function refreshFarmer(farmerId: string, htxId?: string) {
  invalidate(farmerId, htxId);
}

function invalidate(farmerId: string, htxId?: string) {
  byId.invalidate(farmerId);
  byId.get(farmerId);
  if (htxId) {
    byHtx.invalidate(htxId);
    byHtx.get(htxId);
  }
}

export async function updateFarmer(id: string, data: Partial<Farmer>, htxId?: string): Promise<Farmer> {
  const updated = await ekycApi.updateFarmerProfile(id, {
    address: data.address,
    phone: data.phone,
    province: data.tinhThanh,
    district: data.huyen,
    ward: data.xa,
    areaHa: data.dienTich,
    householdMembers: data.soThanhVien,
    mainCrop: data.loaiCay,
    notes: data.ghiChu,
  });

  // status/htxStatus live on a separate endpoint (updateFarmerStatus) — only call it
  // when the caller actually asked to change one, since it's a distinct audit-logged
  // action server-side (farmer activity vs. HTX relationship health).
  const statusPatch: { status?: ekycApi.BackendFarmerStatus; htxRelationStatus?: ekycApi.BackendHtxRelationStatus } = {};
  if (data.status) statusPatch.status = STATUS_TO_BACKEND[data.status];
  if (data.htxStatus) statusPatch.htxRelationStatus = HTX_STATUS_TO_BACKEND[data.htxStatus];
  const final = Object.keys(statusPatch).length > 0 ? await ekycApi.updateFarmerStatus(id, statusPatch) : updated;

  const farmer = toFrontendFarmer(final);
  invalidate(farmer.id, htxId);
  byId.set(farmer.id, farmer);
  return farmer;
}

export async function approveFarmerMembership(farmerId: string, htxId?: string): Promise<Farmer> {
  const updated = await ekycApi.updateFarmerMembership(farmerId, 'APPROVE');
  const farmer = toFrontendFarmer(updated);
  invalidate(farmer.id, htxId);
  byId.set(farmer.id, farmer);
  return farmer;
}

export async function rejectFarmerMembership(farmerId: string, reason: string, htxId?: string): Promise<Farmer> {
  const updated = await ekycApi.updateFarmerMembership(farmerId, 'REJECT', reason);
  const farmer = toFrontendFarmer(updated);
  invalidate(farmer.id, htxId);
  byId.set(farmer.id, farmer);
  return farmer;
}

/** Farmer accepts/declines an HTX's invite to join — self-service. */
export async function respondToHtxInvite(farmerId: string, decision: 'ACCEPT' | 'DECLINE'): Promise<Farmer> {
  const updated = await ekycApi.respondToHtxInvite(farmerId, decision);
  const farmer = toFrontendFarmer(updated);
  byId.invalidate(farmerId);
  byId.set(farmer.id, farmer);
  return farmer;
}

/** Farmer voluntarily leaves their current HTX — self-service. */
export async function leaveHtx(farmerId: string): Promise<Farmer> {
  const updated = await ekycApi.leaveHtx(farmerId);
  const farmer = toFrontendFarmer(updated);
  byId.invalidate(farmerId);
  byId.set(farmer.id, farmer);
  return farmer;
}

/** HTX searches standalone (no-htx) farmers to invite — not cached, this is a
 * one-off search-as-you-type action, not a roster to keep in sync. */
export async function searchStandaloneFarmers(q: string): Promise<Farmer[]> {
  if (!q.trim()) return [];
  const result = await ekycApi.searchStandaloneFarmers(q);
  return result.items.map(toFrontendFarmer);
}

/** HTX invites a standalone farmer to join — the farmer must accept. */
export async function inviteFarmerToHtx(farmerId: string, htxId: string): Promise<Farmer> {
  const updated = await ekycApi.inviteFarmerToHtx(farmerId);
  const farmer = toFrontendFarmer(updated);
  invalidate(farmer.id, htxId);
  byId.set(farmer.id, farmer);
  return farmer;
}

/** Phase 1 "HTX tạo hồ sơ nông hộ trước" — creates a Farmer with no User
 * account yet, already-active (already-approved by construction). */
export async function createFarmerProfileByHtx(
  data: {
    fullName: string;
    cccd?: string;
    address?: string;
    phone?: string;
    province?: string;
    district?: string;
    ward?: string;
    areaHa?: number;
    householdMembers?: number;
    mainCrop?: string;
    startDate?: string;
    attachments?: ekycApi.FarmerAttachmentInput[];
  },
  htxId?: string,
): Promise<{ farmer: Farmer; warning?: string }> {
  const result = await ekycApi.createFarmerProfileByHtx(data);
  const farmer = toFrontendFarmer(result.farmer);
  if (htxId) invalidate(farmer.id, htxId);
  byId.set(farmer.id, farmer);
  return { farmer, warning: result.warning };
}

export async function addFarmerAttachment(farmerId: string, attachment: ekycApi.FarmerAttachmentInput, htxId?: string): Promise<Farmer> {
  const updated = await ekycApi.addFarmerAttachment(farmerId, attachment);
  const farmer = toFrontendFarmer(updated);
  invalidate(farmer.id, htxId);
  byId.set(farmer.id, farmer);
  return farmer;
}

/** HTX's own profile-only (userId-less) farmers — used to check a new join
 * request against a profile HTX already pre-created. Not cached — a one-off
 * lookup at review time, not a roster to keep in sync. */
export async function suggestProfileMatches(pendingFarmerId: string): Promise<Array<Farmer & { matchReasons: string[] }>> {
  const items = await ekycApi.suggestProfileMatches(pendingFarmerId);
  return items.map((f) => ({ ...toFrontendFarmer(f), matchReasons: f.matchReasons }));
}

export async function listProfileOnlyFarmers(htxId: string, q?: string): Promise<Farmer[]> {
  const items = await ekycApi.listProfileOnlyFarmers(htxId, q);
  return items.map(toFrontendFarmer);
}

/** Merges a newly self-registered farmer into an existing HTX-pre-created
 * profile — see EkycService.linkFarmerToExistingProfile for the exact rule.
 * Returns a `warning` alongside the merged farmer when the duplicate had its
 * own season/field setup, so the caller can surface it instead of treating
 * the merge as unconditionally clean. */
export async function mergeFarmerProfiles(duplicateFarmerId: string, targetFarmerId: string, htxId?: string): Promise<{ farmer: Farmer; warning?: string }> {
  const result = await ekycApi.mergeFarmerProfiles(duplicateFarmerId, targetFarmerId);
  const farmer = toFrontendFarmer(result.farmer);
  byId.invalidate(duplicateFarmerId);
  invalidate(farmer.id, htxId);
  byId.set(farmer.id, farmer);
  return { farmer, warning: result.warning };
}
